/**
 * useSegmentPreloader -- progressively preloads HLS video segments ahead of playback.
 *
 * Creates hidden <video> elements with HLS.js instances to warm the browser HTTP cache
 * for segments beyond the current ping-pong pair (activeIndex and activeIndex+1).
 * Loads sequentially: starts at activeIndex+2, waits for canplay, then moves to the next.
 *
 * Shared by MiniTvBaseballExecution and MiniTvFootballExecution.
 */
import { useRef, useEffect, useCallback } from 'react';
import Hls from 'hls.js';
import { ensureAvhForVodUrl } from 'features/video/components/mini-tv/hlsAvh';

const PRELOAD_READY_TIMEOUT_MS = 15000;

function createHlsForPreload(video, url, avh) {
  if (!url || String(url).trim() === '') return null;

  if (url.includes('m3u8') && Hls.isSupported()) {
    const hlsConfig = { enableWorker: true };
    hlsConfig.xhrSetup = (xhr, reqUrl) => {
      const finalUrl = ensureAvhForVodUrl(reqUrl, url, avh);
      if (finalUrl !== reqUrl) xhr.open('GET', finalUrl, true);
    };
    if (typeof Request !== 'undefined') {
      hlsConfig.fetchSetup = (context, initParams) => {
        const finalUrl = ensureAvhForVodUrl(context.url, url, avh);
        return new Request(finalUrl, initParams);
      };
    }
    const hls = new Hls(hlsConfig);
    hls.loadSource(url);
    hls.attachMedia(video);
    return hls;
  }

  video.src = url;
  video.preload = 'auto';
  try { video.load(); } catch { /* ignore */ }
  return null;
}

function destroyPreloadEntry(entry) {
  if (!entry) return;
  try { entry.hls?.destroy(); } catch { /* ignore */ }
  try {
    entry.video?.pause?.();
    entry.video?.removeAttribute?.('src');
    entry.video?.load?.();
  } catch { /* ignore */ }
}

/**
 * @param {string[]|null} videoUrls  Full ordered list of segment URLs
 * @param {unknown}       avh        api.video session token
 * @param {number}        activeIndex Currently playing segment index
 * @returns {{ isReady: (index: number) => boolean, waitForReady: (index: number) => Promise<void> }}
 */
export function useSegmentPreloader(videoUrls, avh, activeIndex) {
  const entriesRef = useRef({});
  const readySetRef = useRef(new Set());
  const waitersRef = useRef({});
  const activeIndexRef = useRef(activeIndex);
  activeIndexRef.current = activeIndex;
  const urlsRef = useRef(videoUrls);
  urlsRef.current = videoUrls;
  const avhRef = useRef(avh);
  avhRef.current = avh;

  const markReady = useCallback((index) => {
    readySetRef.current.add(index);
    const waiter = waitersRef.current[index];
    if (waiter) {
      waiter.resolve();
      if (waiter.timeoutId != null) clearTimeout(waiter.timeoutId);
      delete waitersRef.current[index];
    }
  }, []);

  const startPreloadForIndex = useCallback((index) => {
    const urls = urlsRef.current;
    if (!urls || index >= urls.length) return;
    if (entriesRef.current[index]) return;
    if (readySetRef.current.has(index)) return;

    const url = urls[index];
    if (!url || String(url).trim() === '') return;

    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.setAttribute('aria-hidden', 'true');
    video.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;';

    const hls = createHlsForPreload(video, url, avhRef.current);
    const entry = { video, hls, index, url };
    entriesRef.current[index] = entry;

    const onReady = () => {
      video.removeEventListener('canplay', onReady);
      video.removeEventListener('canplaythrough', onReady);
      markReady(index);
      const nextIdx = index + 1;
      const ai = activeIndexRef.current;
      if (nextIdx > ai + 1 && urlsRef.current && nextIdx < urlsRef.current.length) {
        startPreloadForIndex(nextIdx);
      }
    };

    video.addEventListener('canplay', onReady);
    video.addEventListener('canplaythrough', onReady);
    if (video.readyState >= 2) onReady();
  }, [markReady]);

  useEffect(() => {
    const urls = videoUrls;
    if (!urls || urls.length === 0) return;

    const firstPreloadIdx = activeIndex + 2;
    if (firstPreloadIdx < urls.length) {
      startPreloadForIndex(firstPreloadIdx);
    }
  }, [videoUrls, activeIndex, startPreloadForIndex]);

  useEffect(() => {
    const entries = entriesRef.current;
    return () => {
      for (const key of Object.keys(entries)) {
        destroyPreloadEntry(entries[key]);
        delete entries[key];
      }
      readySetRef.current.clear();
      for (const key of Object.keys(waitersRef.current)) {
        const w = waitersRef.current[key];
        if (w?.timeoutId != null) clearTimeout(w.timeoutId);
      }
      waitersRef.current = {};
    };
  }, []);

  const isReady = useCallback((index) => readySetRef.current.has(index), []);

  const waitForReady = useCallback((index) => {
    if (readySetRef.current.has(index)) return Promise.resolve();
    if (waitersRef.current[index]) return waitersRef.current[index].promise;

    let resolve;
    const promise = new Promise((r) => { resolve = r; });
    const timeoutId = setTimeout(() => {
      resolve();
      delete waitersRef.current[index];
    }, PRELOAD_READY_TIMEOUT_MS);
    waitersRef.current[index] = { promise, resolve, timeoutId };
    return promise;
  }, []);

  return { isReady, waitForReady };
}
